import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import type { EventSetting, PortalPayload } from "../lib/api";

// LŪMEN-style hero, rebuilt as a responsive system.
//
// Root-cause fixes vs. the old fixed-coordinate version:
//  * Fluid `clamp()` typography instead of fixed `text-[68px]` + `top-[178px]`.
//  * Annotations ([ EVENT_DATE ] / [ FOOD_MENU ] / [ TRANSPORT ]) live in normal
//    document flow as real, keyboard-focusable link chips — they can never collide
//    with the heading, artwork or each other at any viewport.
//  * The floating node squares + connector lines (which drifted and "pointed
//    nowhere" at other aspect ratios) are gone; only a faint grid + a couple of
//    geometric accents remain as decoration on wide screens, off the text path.
//  * A left/bottom scrim guarantees copy + CTA contrast over the artwork.
//  * The info card + primary CTA are a single flex row that stacks when it no
//    longer fits, so they can never cover each other. The "NOT A CLASS" tag is a
//    header band INSIDE the card (no more straddling the border).
//
// Three compositions emerge from the same intrinsic flow:
//   A) immersive portrait  (< lg)   — stacked, art immersive behind.
//   B) compact landscape   (lg–xl)  — tighter vertical rhythm, decoration hidden.
//   C) wide desktop        (xl+)    — editorial grid + geometric accents.

/* ---------------- Decorative (wide-only, never on the text path) ---------------- */
function GridAccents() {
  return (
    <div className="pointer-events-none absolute inset-0 hidden xl:block" aria-hidden="true">
      {/* fine grid, very faint texture only */}
      {["22%", "50%", "78%"].map((left, i) => (
        <div
          key={`v-${left}`}
          className="absolute top-0 h-full w-px bg-white/[0.05] anim-grid-v"
          style={{ left, animationDelay: `${400 + i * 100}ms` }}
        />
      ))}
      {["30%", "68%"].map((top, i) => (
        <div
          key={`h-${top}`}
          className="absolute left-0 h-px w-full bg-white/[0.05] anim-grid-h"
          style={{ top, animationDelay: `${600 + i * 150}ms` }}
        />
      ))}
      {/* Intentional crop-mark framing brackets in the right margin (geometric accent).
          These are a deliberate editorial motif, not orphaned debug squares. */}
      <div
        className="anim-fade-in absolute right-[3.5%] top-[11%] h-[120px] w-[120px]"
        style={{ animationDelay: "1100ms" }}
      >
        {[
          "left-0 top-0 border-l-2 border-t-2",
          "right-0 top-0 border-r-2 border-t-2",
          "left-0 bottom-0 border-b-2 border-l-2",
          "right-0 bottom-0 border-b-2 border-r-2",
        ].map((cls) => (
          <span key={cls} className={`absolute h-4 w-4 border-white/30 ${cls}`} />
        ))}
      </div>
    </div>
  );
}

/* ---------------- Meaningful annotation chips (in flow, always readable) -------- */
const ANNOTATIONS = [
  {
    to: "/voting",
    label: "EVENT_DATE",
    body: "Choose the date that works for the whole batch.",
  },
  {
    to: "/menu",
    label: "FOOD_MENU",
    body: "See the menu everyone will enjoy.",
  },
  {
    to: "/budget",
    label: "TRANSPORT",
    body: "Pickup & return arrangements and their costs.",
  },
];

function AnnotationChip({
  to,
  label,
  body,
  delay,
}: {
  to: string;
  label: string;
  body: string;
  delay: number;
}) {
  return (
    <Link
      to={to}
      style={{ animationDelay: `${delay}ms` }}
      className="anim-fade-up group flex min-w-[220px] flex-1 basis-[240px] flex-col gap-2 border border-white/12 bg-white/[0.03] p-4 transition-colors hover:border-accent/70 hover:bg-accent/[0.06] focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/40"
    >
      <span className="font-manrope text-[13px] leading-none text-white">
        [ {label} ]
      </span>
      <p className="font-manrope text-[12.5px] leading-[1.55] text-white/60">
        {body}
        <span className="ml-1 inline-block text-accent transition-transform group-hover:translate-x-1">
          &rarr;
        </span>
      </p>
    </Link>
  );
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function Hero({
  event,
  decisionStatus,
}: {
  event: EventSetting;
  decisionStatus: PortalPayload["decisionStatus"];
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoAllowed, setVideoAllowed] = useState(!prefersReducedMotion());

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setVideoAllowed(!mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!videoAllowed) video.pause();
    else video.play().catch(() => setVideoFailed(true));
  }, [videoAllowed]);

  const showVideo = !videoFailed && videoAllowed;

  return (
    <section id="top" className="relative min-h-[100dvh] w-full overflow-hidden bg-black">
      {/* Background video (layer 0) */}
      {showVideo ? (
        <video
          ref={videoRef}
          className="anim-fade-in absolute inset-0 h-full w-full object-cover"
          src={event.videoUrl}
          poster="/hero-poster-dark.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onError={() => setVideoFailed(true)}
          aria-hidden="true"
        />
      ) : (
        <img
          src="/hero-poster-dark.jpg"
          alt=""
          aria-hidden="true"
          className="anim-fade-in absolute inset-0 h-full w-full object-cover"
        />
      )}

      {/* Text-safe scrims: darker at left + bottom so copy/CTA always read */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.93) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0.28) 70%, rgba(0,0,0,0.42) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%]"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      <GridAccents />

      {/* Content layer — intrinsic flow, vertically centred, safe area padded */}
      <div className="hero-content relative z-10 mx-auto flex min-h-[100dvh] w-full max-w-shell flex-col justify-center px-5 pb-16 pt-28 sm:px-8 md:px-10 md:pt-32 lg:px-12">
        <div className="flex flex-col gap-8 xl:flex-row xl:items-end xl:justify-between xl:gap-12">
          {/* Heading + copy */}
          <div className="max-w-[640px]">
            <h1
              className="anim-fade-up font-graphik font-normal tracking-[-0.01em] text-white"
              style={{
                animationDelay: "350ms",
                fontSize: "clamp(2.25rem, 5vw + 0.25rem, 4.5rem)",
                lineHeight: 1.02,
                maxWidth: "13ch",
                textShadow: "0 2px 40px rgba(0,0,0,0.75), 0 1px 3px rgba(0,0,0,0.6)",
              }}
            >
              {event.eventName}
            </h1>
            <p
              className="anim-fade-up mt-5 font-manrope text-white/80"
              style={{
                animationDelay: "500ms",
                fontSize: "clamp(0.9375rem, 0.9vw + 0.55rem, 1.0625rem)",
                lineHeight: 1.65,
                maxWidth: "48ch",
                textShadow: "0 1px 22px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.7)",
              }}
            >
              Everything you need to know about the BBA Freshers&rsquo; Party. Explore event
              plans, vote on important decisions, compare the budget, and help us organize an
              event everyone can enjoy.
            </p>
          </div>

          {/* Primary CTA + info card: one row that stacks; never overlap */}
          <div className="flex w-full max-w-[520px] flex-col gap-5 lg:max-w-[380px]">
            <Link
              to="/voting"
              className="anim-fade-up group inline-flex w-fit items-center gap-2.5 bg-accent px-6 py-3.5 transition-colors hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              style={{ animationDelay: "750ms" }}
            >
              <span className="text-[15px] leading-none text-black">&#10022;</span>
              <span className="font-manrope text-[13px] font-semibold uppercase leading-none tracking-[0.12em] text-black">
                Cast Your Votes
              </span>
            </Link>

            {/* Info card */}
            <div
              className="anim-fade-up relative w-full"
              style={{ animationDelay: "900ms" }}
            >
              <div className="border border-accent/35 bg-black/40 backdrop-blur-[2px]">
                <div className="bg-accent px-4 py-1.5">
                  <span className="font-manrope text-[11.5px] font-semibold uppercase leading-none tracking-[0.14em] text-black">
                    NOT A CLASS &mdash; A CELEBRATION
                  </span>
                </div>
                <div className="p-5">
                  <p className="font-manrope text-[13px] leading-[1.55] text-white/85">
                    Your vote matters. Help us finalize the date, menu, contribution, and
                    transportation arrangements.... Expected {event.expectedAttendees} of{" "}
                    {event.totalCohort} students.
                  </p>
                  <Link
                    to="/budget"
                    className="mt-4 inline-block font-manrope text-[12px] font-semibold uppercase leading-none tracking-[0.12em] text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                  >
                    EXPLORE_BUDGET_TRANSPARENCY
                  </Link>
                </div>
                {/* editorial chamfer accent (bottom-left) */}
                <div
                  className="pointer-events-none absolute bottom-0 left-0 h-0 w-0"
                  style={{
                    borderStyle: "solid",
                    borderWidth: "0 0 22px 22px",
                    borderColor: "transparent transparent rgba(175,221,255,0.5) transparent",
                  }}
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Meaningful annotations — always readable, never collide */}
        <div className="mt-8 flex flex-wrap gap-3.5 lg:mt-10">
          {ANNOTATIONS.map((a, i) => (
            <AnnotationChip key={a.label} {...a} delay={1050 + i * 120} />
          ))}
        </div>

        {/* Screen-reader-only decision status (visual equivalents live above) */}
        <p className="sr-only">
          Decision status: {decisionStatus.openPolls} polls open, {decisionStatus.decidedPolls} of{" "}
          {decisionStatus.totalPolls} decided.
        </p>
      </div>
    </section>
  );
}
